import React, { useContext, useState } from "react";
import { CommandBarButton, CommandBarButtonMenuItem, ListActionButtonProps, TooltipHost } from "sprint-common-web";

import { DeliveryInfoAside } from "../../../../common/components/delivery-info-button-aside/DeliveryInfoAside";
import { AppConfigContext } from "../../../../core/configuration/Configuration";
import { AuthorizationWrapper } from "../../../../core/security/AuthorizationWrapper";
import { SprintAccessRight } from "../../../../core/security/SprintAccessRight";
import { useAccessVerifier } from "../../../../core/security/useAccessVerifier";
import {
    CloudFileStatus,
    isInvalidStatusForQcDone,
    isInvalidStatusForReject,
    isInvalidStatusForWaitingForClientResponse
} from "../../common/types/CloudFileStatus";
import { RemoveCloudFileButtonDialog } from "../abandon/AbandonCloudFileButtonDialog";
import { ApproveCloudFileNotificationsListingDialog } from "../buttons/approve-notifications/ApproveCloudFileNotificationsListingDialog";
import { ChangeCloudFileToQcDoneListingDialog } from "../buttons/change-to-qc-done/ChangeCloudFileToQcDoneListingDialog";
import { CloudFileStatusDetailsButtonAside } from "../buttons/details/CloudFileStatusDetailsButtonAside";
import { RejectCloudFileListingDialog } from "../buttons/reject/reject-single/RejectCloudFileListingDialog";
import { CloudFileWaitingForClientResponseListingDialog } from "../buttons/request-waiting-for-client-response/single/CloudFileWaitingForClientResponseListingDialog";
import { RepeatProcessingButtonDialog } from "../repeat-processing/RepeatProcessingButtonDialog";
import { RequestCloudFileFixButtonDialog } from "../request-cloud-fix/RequestCloudFileFixButtonDialog";
import { CloudFileListingItem } from "../types/CloudFileListingItem";

type CloudFileActionsListingButtonsProps = ListActionButtonProps<CloudFileListingItem>;

enum CloudFileActions {
    ShowDeliveryInfo,
    ShowStatusDetails,
    ShowWorkflowDetails,
    RepeatFailedProcessing,
    ApproveNotifications,
    Remove,
    RequestCloudFix,
    ChangeToQcDone,
    WaitingForClientResponse,
    Reject
}

export const CloudFileActionsListingButtons = ({ item }: CloudFileActionsListingButtonsProps): JSX.Element => {
    const { baseExecutionDetailsUri, cloudFixEnabled } = useContext(AppConfigContext);

    const [state, setState] = useState<CloudFileActions>();

    const { isAuthorized } = useAccessVerifier();

    const getMenuItems = (): CommandBarButtonMenuItem[] => {
        const items: CommandBarButtonMenuItem[] = [];

        items.push(
            ...[
                {
                    text: "Delivery info",
                    onClick: () => setState(CloudFileActions.ShowDeliveryInfo)
                },
                {
                    text: "Status",
                    subMenuProps: {
                        items: [
                            {
                                text: "Waiting for Client Response",
                                onClick: () => setState(CloudFileActions.WaitingForClientResponse),
                                disabled: isInvalidStatusForWaitingForClientResponse(item.status)
                            },
                            {
                                text: "Qc Done",
                                onClick: () => setState(CloudFileActions.ChangeToQcDone),
                                disabled: isInvalidStatusForQcDone(item.status)
                            },
                            {
                                text: "Reject",
                                onClick: () => setState(CloudFileActions.Reject),
                                disabled: isInvalidStatusForReject(item.status)
                            }
                        ]
                    }
                },
                {
                    text: "Approve notifications",
                    disabled: item.isNotificationApproved,
                    onClick: () => setState(CloudFileActions.ApproveNotifications)
                }
            ]
        );

        items.push(
            ...[
                {
                    text: "Status details",
                    disabled: !item?.statusDetails,
                    onClick: () => setState(CloudFileActions.ShowStatusDetails)
                },
                {
                    text: "Repeat failed processing",
                    disabled: item.status !== CloudFileStatus.PrepQcFailed,
                    onClick: () => setState(CloudFileActions.RepeatFailedProcessing)
                },
                {
                    text: "Remove",
                    disabled: !!item.isNotificationApproved,
                    onClick: () => setState(CloudFileActions.Remove)
                }
            ]
        );

        if (isAuthorized([SprintAccessRight.CiAdmin, SprintAccessRight.MonitRead])) {
            items.push({
                text: "Execution details",
                disabled: !item.executionArn,
                onClick: () => window.open(`${baseExecutionDetailsUri}/${item.executionArn}`)
            });
        }

        if (cloudFixEnabled) {
            items.push({
                text: "Request cloud fix",
                onClick: () => setState(CloudFileActions.RequestCloudFix)
            });
        }

        return items;
    };

    return (
        <AuthorizationWrapper accessRights={[SprintAccessRight.CiReadWrite]}>
            <TooltipHost content="Additional">
                <CommandBarButton icon="MoreVertical" menuItems={getMenuItems()} />
            </TooltipHost>
            <CloudFileDialogManager status={state} item={item} onClose={() => setState(undefined)} />
        </AuthorizationWrapper>
    );
};

const CloudFileDialogManager = ({
    status,
    item,
    onClose
}: {
    status?: CloudFileActions;
    item: CloudFileListingItem;
    onClose: () => void;
}) => {
    switch (status) {
        case CloudFileActions.ApproveNotifications:
            return <ApproveCloudFileNotificationsListingDialog isOpen itemId={item.id} onClose={onClose} />;
        case CloudFileActions.ShowDeliveryInfo:
            return <DeliveryInfoAside isOpen onClose={onClose} deliveryId={item.deliveryId} />;
        case CloudFileActions.ShowStatusDetails:
            return <CloudFileStatusDetailsButtonAside isOpen onClose={onClose} itemId={item.id} />;
        case CloudFileActions.RepeatFailedProcessing:
            return <RepeatProcessingButtonDialog isOpen onClose={onClose} itemId={item.id} filePath={item.filePath} />;
        case CloudFileActions.Remove:
            return <RemoveCloudFileButtonDialog isOpen onClose={onClose} itemId={item.id} />;
        case CloudFileActions.ChangeToQcDone:
            return (
                <ChangeCloudFileToQcDoneListingDialog
                    isOpen
                    onClose={onClose}
                    cloudFileId={item.id}
                    cloudFileRecordType={item.recordType}
                    fileRecordId={item.fileRecordId}
                />
            );
        case CloudFileActions.RequestCloudFix:
            return <RequestCloudFileFixButtonDialog isOpen onClose={onClose} item={item} />;
        case CloudFileActions.WaitingForClientResponse:
            return <CloudFileWaitingForClientResponseListingDialog isOpen onClose={onClose} cloudFileId={item.id} />;
        case CloudFileActions.Reject:
            return <RejectCloudFileListingDialog onClose={onClose} cloudFileId={item.id} />;
        default:
            return <></>;
    }
};
